'use client'
import React, { ChangeEvent, useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { Eye, EyeOff } from 'react-feather'
import axiosReq from '../interceptor'
import { encryptData } from '../utils/helpers'
import FormBackgroundLayout from '../components/form-background-layout'
import '../onboarding/onboarding.css'
import useDismissAlert from '../Hooks/useDismissAlert'
import { useDispatch } from 'react-redux'
import { resetState } from '../app-store/businessDataSlice'
import { MessageAlert } from '../components/message-alert'
import TwoFactorVerificationModal from '../dashboard/settings/modals/2fa-verification-modal'

const Login: React.FC = () => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [areFieldsSet, setAreFieldsSet] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [error, setError] = useDismissAlert({ email: '', password: '' }, 5000)
  const [loginError, setLoginError] = useDismissAlert({ message: '' }, 5000)
  const [show2FAModal, setShow2FAModal] = useState(false)
  const [preAuthToken, setPreAuthToken] = useState('')
  const isSubmitting = useRef(false)
  const router = useRouter()

  const isTokenValid = (token: string): boolean => {
    try {
      const base64Url = token.split('.')[1]
      if (base64Url == null) return false

      // Convert base64url to base64.
      const base64 = base64Url
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=')

      const payload = JSON.parse(atob(base64))
      return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now()
    } catch {
      return false
    }
  }
  // Check for existing valid token on page load.
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token && isTokenValid(token)) {
      router.push('/dashboard/overview')
    } else if (token) {
      localStorage.removeItem('token')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setAreFieldsSet(formData.email !== '' && formData.password !== '')
  }, [formData.email, formData.password])

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
    const startsWithNumber = /^\d/.test(email)
    return emailRegex.test(email) && !startsWithNumber
  }

  const validateForm = () => {
    let valid = true
    const error: { email: string; password: string } = {
      email: '',
      password: ''
    }

    if (!validateEmail(formData.email)) {
      error.email = 'Please enter a valid email.'
      valid = false
    }

    if (formData.password === '') {
      error.password = 'Password is required.'
      valid = false
    }

    setError(error)
    return valid
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setLoginError({ message: '' })
  }

  const handle2FASuccess = (
    token?: string,
    _expiresIn?: number,
    requiresAuthenticatorSetup?: boolean
  ) => {
    setShow2FAModal(false)

    if (token) {
      localStorage.setItem('token', token)
    }
    localStorage.removeItem('preAuthToken')
    localStorage.removeItem('tempUser')
    setPreAuthToken('')

    if (requiresAuthenticatorSetup === true) {
      localStorage.setItem('requires_authenticator_setup', 'true')
      router.push('/dashboard/settings?tab=security&reason=backup_login')
      return
    }

    const businessStr = localStorage.getItem('business')
    const business = businessStr ? JSON.parse(businessStr) : null

    if (business?.kyc_status === 'approved') {
      router.push('/dashboard/overview')
    } else {
      router.push('/dashboard/kyc')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isSubmitting.current) {
      return
    }
    isSubmitting.current = true
    localStorage.clear()
    sessionStorage.clear()
    dispatch(resetState())

    if (!validateForm()) {
      isSubmitting.current = false
      return
    }

    setIsLoading(true)

    const payload = {
      ...formData,
      email: formData.email.toLowerCase()
    }

    try {
      const { status, data } = await axiosReq.post(
        '/app/v2/auth/sign-in',
        payload
      )
      if (status === 200) {
        const {
          hasEnabled2FA,
          requires2FASetup,
          preAuthToken,
          token,
          user,
          business
        } = data

        // User has not setup 2FA.
        if (!hasEnabled2FA) {
          if (token) {
            localStorage.setItem('token', token)
          }
          localStorage.setItem('uo', encryptData(user))
          if (business) {
            localStorage.setItem('business', JSON.stringify(business))
          }

          setIsLoading(false)

          if (requires2FASetup === true) {
            router.push('/dashboard/settings?tab=security')
          } else {
            if (business?.kyc_status === 'approved') {
              router.push('/dashboard/overview')
            } else {
              router.push('/dashboard/kyc')
            }
          }
          return
        }

        // User has 2FA enabled and token is still valid.
        if (hasEnabled2FA && token) {
          localStorage.setItem('token', token)
          localStorage.setItem('uo', encryptData(user))
          if (business) {
            localStorage.setItem('business', JSON.stringify(business))
          }

          setIsLoading(false)

          if (business?.kyc_status === 'approved') {
            router.push('/dashboard/overview')
          } else {
            router.push('/dashboard/kyc')
          }
          return
        }

        // Flow 3: User has 2FA enabled, token expired — require 2FA verification.
        if (hasEnabled2FA && preAuthToken) {
          localStorage.setItem('preAuthToken', preAuthToken)
          localStorage.setItem('tempUser', encryptData(user))
          if (business) {
            localStorage.setItem('business', JSON.stringify(business))
          }

          setPreAuthToken(preAuthToken)
          setShow2FAModal(true)
          setIsLoading(false)
          return
        }

        console.warn('Unhandled login response:', data)
        setIsLoading(false)
      }
    } catch (error: any) {
      setIsLoading(false)

      if (error.response) {
        const { status, data } = error.response

        if (status === 400) {
          if (data && typeof data.message === 'string') {
            setError({ email: '', password: data.message })
          } else if (data && data.errors) {
            setError({
              email: data.errors.email || '',
              password: data.errors.password || ''
            })
          }
        } else if (status === 423) {
          setLoginError({
            message: `Account locked until ${new Date(data.lockedUntil).toLocaleString()}`
          })
        } else {
          setLoginError({
            message:
              'Login failed. Please check your email and password and try again.'
          })
        }
      } else {
        setLoginError({
          message:
            'Network error. Please check your internet connection and try again later.'
        })
      }
    } finally {
      isSubmitting.current = false
    }
  }

  return (
    <>
      <FormBackgroundLayout
        title="Welcome"
        subtitle={
          <>
            New here?{' '}
            <Link prefetch href="/onboarding" className="text-white">
              Create Account
            </Link>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-outline mb-4">
            <label className="form-label text-white" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-control mb-3"
              placeholder="Enter your registered email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            {error.email && <MessageAlert message={error.email} type="error" />}
          </div>

          <div className="form-outline mb-4 position-relative">
            <label className="form-label text-white" htmlFor="password">
              Password
            </label>
            <div className="input-group mb-3">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                className="form-control"
                placeholder="Enter your password"
                autoComplete="off"
                required
                value={formData.password}
                onChange={handleChange}
                style={{ borderRadius: '0.375rem' }}
              />
              <div
                className="input-group-append"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </div>
            </div>
            {error.password && (
              <MessageAlert message={error.password} type="error" />
            )}
          </div>

          <div className="text-end mb-4">
            <Link
              prefetch
              href="/forgot-password"
              className="text-white-50"
              style={{ fontSize: '14px' }}
            >
              Forgot Password?
            </Link>
          </div>

          {loginError.message && (
            <MessageAlert message={loginError.message} type="error" />
          )}

          <button
            className="btn btn-primary rounded-pill w-100 btn-block"
            type="submit"
            disabled={isLoading || !areFieldsSet}
          >
            {isLoading ? 'Logging you in...' : 'Login'}
          </button>
        </form>

        {isLoading && (
          <div className="mt-4">
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </FormBackgroundLayout>

      <TwoFactorVerificationModal
        isOpen={show2FAModal}
        onClose={() => {
          setShow2FAModal(false)
          setIsLoading(false)
          localStorage.removeItem('preAuthToken')
          localStorage.removeItem('tempUser')
          setPreAuthToken('')
        }}
        onSuccess={handle2FASuccess}
        preAuthToken={preAuthToken}
        email={formData.email}
        purpose="login"
      />
    </>
  )
}

export default Login
